import React, { useState, useEffect } from 'react';
import { Smartphone, Download, Sparkles, Check, Share, PlusSquare, ArrowRight, ShieldCheck, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function AppDownloadSection() {
  const { lang } = useLanguage();
  const [deviceOS, setDeviceOS] = useState('desktop'); // 'ios' | 'android' | 'desktop'
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    if (isIOS) setDeviceOS('ios');
    else if (isAndroid) setDeviceOS('android');
    else setDeviceOS('desktop');

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      window.open('https://play.google.com/store/apps/details?id=nl.aurahair.app', '_blank');
    }
  };

  return (
    <section className="w-full py-12 sm:py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden border-t border-amber-500/30">
      
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-amber-500/10 blur-[130px] pointer-events-none rounded-full" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🍏 ONLY IOS / IPHONE VIEW */}
        {deviceOS === 'ios' && (
          <div className="p-6 sm:p-10 rounded-3xl bg-slate-900/90 border-2 border-amber-500/50 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 max-w-xl text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-300 text-xs font-black uppercase tracking-wider">
                <span>🍏</span>
                <span>{lang === 'nl' ? 'Exclusief voor iPhone & iPad' : lang === 'tr' ? 'iPhone ve iPad İçin Özel' : 'Exclusive for iPhone & iPad'}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black font-serif text-white tracking-tight">
                {lang === 'nl' ? 'Aura Hair Afspraken op uw iPhone' : lang === 'tr' ? 'Aura Hair Randevu iPhone Uygulaması' : 'Aura Hair Booking on your iPhone'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                {lang === 'nl'
                  ? 'Installeer direct via Safari. Geen App Store nodig. Opent altijd supersnel en direct in de afspraakmodule zonder de hele website!'
                  : lang === 'tr'
                  ? 'Safari üzerinden ana ekranınıza ekleyin. Tıkladığınızda web sitesi değil, doğrudan sadece randevu verme ekranı açılır!'
                  : 'Install directly via Safari. No App Store needed. Opens instantly into the booking module without loading the full site!'}
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowIOSModal(true)}
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 hover:scale-105 transition-all cursor-pointer"
                >
                  <PlusSquare className="w-4 h-4 stroke-[2.5]" />
                  <span>{lang === 'nl' ? 'Hoe installeren? (2 Stappen)' : lang === 'tr' ? 'iPhone\'a Nasıl Yüklenir?' : 'How to Install? (2 Steps)'}</span>
                </button>
              </div>
            </div>

            <div className="w-44 sm:w-52 h-44 sm:h-52 rounded-3xl bg-gradient-to-tr from-amber-500 to-yellow-400 p-1 shadow-2xl shrink-0 flex items-center justify-center">
              <div className="w-full h-full rounded-[22px] bg-slate-950 flex flex-col items-center justify-center p-4 text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-3xl">
                  ✂️
                </div>
                <strong className="text-white text-xs font-black">Aura Hair</strong>
                <span className="text-[10px] text-amber-400 font-bold bg-amber-400/10 px-2 py-0.5 rounded">
                  {lang === 'nl' ? 'Afspraken App' : lang === 'tr' ? 'Randevu App' : 'Booking App'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 🤖 ONLY ANDROID VIEW */}
        {deviceOS === 'android' && (
          <div className="p-6 sm:p-10 rounded-3xl bg-slate-900/90 border-2 border-emerald-500/50 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 max-w-xl text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-black uppercase tracking-wider">
                <span>🤖</span>
                <span>{lang === 'nl' ? 'Exclusief voor Android' : lang === 'tr' ? 'Android İçin Özel' : 'Exclusive for Android'}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black font-serif text-white tracking-tight">
                {lang === 'nl' ? 'Aura Hair Afspraken op uw Android' : lang === 'tr' ? 'Aura Hair Randevu Android Uygulaması' : 'Aura Hair Booking on your Android'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                {lang === 'nl'
                  ? 'Download via Google Play of installeer met 1 klik op uw startscherm. Opent direct uitsluitend de afspraakmodule!'
                  : lang === 'tr'
                  ? 'Google Play üzerinden indirin veya tek tıkla ana ekranınıza kurun. Açtığınızda web sitesini değil, doğrudan sadece randevu verme ekranını açar!'
                  : 'Download via Google Play or install with 1 tap to your home screen. Opens directly into the booking screen!'}
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  onClick={handleAndroidInstall}
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 hover:scale-105 transition-all cursor-pointer"
                >
                  <Download className="w-4 h-4 stroke-[3]" />
                  <span>{lang === 'nl' ? '⚡ 1-Klik Installeren' : lang === 'tr' ? '⚡ Tek Tıkla Telefona Kur' : '⚡ 1-Tap Install'}</span>
                </button>
                <a
                  href="https://play.google.com/store/apps/details?id=nl.aurahair.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-5 py-3 rounded-xl bg-slate-950 border border-slate-700 hover:border-amber-400 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
                >
                  <span>▶️</span>
                  <span>Google Play Store</span>
                </a>
              </div>
            </div>

            <div className="w-44 sm:w-52 h-44 sm:h-52 rounded-3xl bg-gradient-to-tr from-emerald-500 to-teal-400 p-1 shadow-2xl shrink-0 flex items-center justify-center">
              <div className="w-full h-full rounded-[22px] bg-slate-950 flex flex-col items-center justify-center p-4 text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-3xl">
                  🤖
                </div>
                <strong className="text-white text-xs font-black">Aura Hair</strong>
                <span className="text-[10px] text-emerald-400 font-bold bg-emerald-400/10 px-2 py-0.5 rounded">
                  {lang === 'nl' ? 'Android App' : lang === 'tr' ? 'Android App' : 'Android App'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 💻 DESKTOP VIEW */}
        {deviceOS === 'desktop' && (
          <div className="p-6 sm:p-10 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-950 border border-amber-400/40 text-amber-300 text-xs font-black uppercase tracking-wider">
              <Smartphone className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'nl' ? 'Aura Hair Mobiele Apps' : lang === 'tr' ? 'Aura Hair Mobil Uygulamaları' : 'Aura Hair Mobile Apps'}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black font-serif text-white tracking-tight">
              {lang === 'nl' ? 'Boek Nog Sneller Met Onze Telefoon App' : lang === 'tr' ? 'Telefon Uygulamamızla Daha Hızlı Randevu Alın' : 'Book Faster with Our Mobile App'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto">
              {lang === 'nl'
                ? 'Bezoek rotterdamaurahair.com op uw iPhone of Android telefoon om de app direct op uw startscherm te installeren.'
                : lang === 'tr'
                ? 'Uygulamayı telefonunuza yüklemek için rotterdamaurahair.com adresini iPhone veya Android telefonunuzdan açın.'
                : 'Visit rotterdamaurahair.com on your iPhone or Android phone to install the app directly on your home screen.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto pt-2">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-center gap-3">
                <span className="text-3xl">🍏</span>
                <div className="text-left">
                  <strong className="text-white text-xs block">iPhone (iOS)</strong>
                  <span className="text-[11px] text-slate-400">
                    {lang === 'nl' ? 'Safari • Zet op beginscherm' : lang === 'tr' ? 'Safari • Ana Ekrana Ekle' : 'Safari • Add to Home Screen'}
                  </span>
                </div>
              </div>
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-center gap-3">
                <span className="text-3xl">🤖</span>
                <div className="text-left">
                  <strong className="text-white text-xs block">Android</strong>
                  <span className="text-[11px] text-slate-400">Google Play &bull; PWA</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* iOS Modal */}
      {showIOSModal && (
        <div onClick={() => setShowIOSModal(false)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in text-white">
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border-2 border-amber-500/60 p-6 rounded-3xl w-full max-w-md space-y-4 shadow-2xl relative my-auto text-left">
            <h3 className="font-serif text-base font-black text-white flex items-center gap-2">
              <span>🍏</span>
              <span>{lang === 'nl' ? 'iPhone Installatiegids' : lang === 'tr' ? 'iPhone\'a Yükleme Rehberi' : 'iPhone Installation Guide'}</span>
            </h3>
            <div className="space-y-3 text-xs text-slate-300">
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-amber-500 text-slate-950 font-black flex items-center justify-center shrink-0">1</div>
                <div>
                  {lang === 'nl' ? (
                    <>Tik onderaan in Safari op het <strong>Deel-icoon</strong> (vierkant met omhoog pijltje ⬆️).</>
                  ) : lang === 'tr' ? (
                    <>Safari'nin altındaki <strong>Paylaş</strong> simgesine dokunun (kare içinde yukarı ok ⬆️).</>
                  ) : (
                    <>Tap the <strong>Share</strong> icon at the bottom of Safari (square with up arrow ⬆️).</>
                  )}
                </div>
              </div>
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500 text-slate-950 font-black flex items-center justify-center shrink-0">2</div>
                <div>
                  {lang === 'nl' ? (
                    <>Kies <strong>"Zet op beginscherm"</strong>.</>
                  ) : lang === 'tr' ? (
                    <><strong>"Ana Ekrana Ekle"</strong> seçeneğine tıklayın.</>
                  ) : (
                    <>Tap <strong>"Add to Home Screen"</strong>.</>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-amber-300">
                {lang === 'nl'
                  ? '✨ Wanneer geopend vanaf uw beginscherm, opent direct uitsluitend de afspraakmodule!'
                  : lang === 'tr'
                  ? '✨ Telefonunuzun ana ekranından açtığınızda sadece randevu bölümü açılır!'
                  : '✨ When opened from your home screen, it launches straight into the booking module!'}
              </p>
            </div>
            <button type="button" onClick={() => setShowIOSModal(false)} className="w-full py-2.5 rounded-xl bg-amber-500 text-slate-950 font-black text-xs uppercase cursor-pointer">
              {lang === 'nl' ? 'Begrepen' : lang === 'tr' ? 'Tamam, Anladım' : 'Got it'}
            </button>
          </div>
        </div>
      )}

    </section>
  );
}
